import React, { useMemo, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, DownloadIcon, FileSpreadsheetIcon, BriefcaseIcon, LockIcon, TrendingUpIcon, DollarSignIcon, ActivityIcon, UserIcon, BarChart3Icon, XIcon, CalendarIcon, FilterIcon, ListIcon, LayoutGridIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Service } from '../../types';
import { formatCurrency, getToday } from '../../utils/helpers';
import { exportServicesToExcel } from '../../utils/excelExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { hasPermission } from '../../types';
const COMPARE_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1'];
export function Services() {
  const {
    services,
    addService,
    updateService,
    deleteService,
    doctors,
    appointments,
    getDoctor
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('services');
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const canViewStats = hasPermission(user?.role || '', 'services_stats', 'read');
  // Tab State
  const [activeTab, setActiveTab] = useState('gestion');
  // Management State
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Analytics State
  const [compareServiceIds, setCompareServiceIds] = useState<string[]>([]);
  const [compareSearchTerm, setCompareSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth() - 5, 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [groupBy, setGroupBy] = useState('month'); // 'day', 'week', 'month'
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    duration: 30,
    price: '',
    description: '',
    doctorIds: [] as string[]
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Filtered services for Management
  const filteredServices = services.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const {
    paginatedItems: paginatedServices,
    currentPage: servicesPage,
    totalPages: servicesTotalPages,
    totalItems: servicesTotalItems,
    startIndex: servicesStartIndex,
    endIndex: servicesEndIndex,
    goToPage: servicesGoToPage,
    nextPage: servicesNextPage,
    prevPage: servicesPrevPage
  } = usePagination(filteredServices);
  // Filtered services for Analytics Selector
  const filteredCompareServices = services.filter(s => s.name.toLowerCase().includes(compareSearchTerm.toLowerCase()));
  // --- ANALYTICS CALCULATIONS ---
  // 1. Filter appointments based on date range and status
  const filteredAppointments = useMemo(() => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    end.setHours(23, 59, 59, 999); // End of the day
    return appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return apt.status === 'completada' && aptDate >= start && aptDate <= end;
    });
  }, [appointments, dateRange]);
  // 2. Aggregate data based on Group By and Selected Services
  const chartData = useMemo(() => {
    const dataMap: Record<string, any> = {};
    // Helper to generate keys based on group by
    const getKey = (date: Date) => {
      if (groupBy === 'day') return date.toISOString().split('T')[0];
      if (groupBy === 'week') {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + 4 - (d.getDay() || 7));
        const yearStart = new Date(d.getFullYear(), 0, 1);
        const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
        return `${d.getFullYear()}-W${weekNo}`;
      }
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // month
    };
    // Initialize map with 0s for selected services if needed, or just build dynamically
    // To ensure continuous timeline, we might need to generate all keys in range, but for simplicity let's map existing data first.
    filteredAppointments.forEach(apt => {
      // If services are selected, filter by them. If none selected, show all (or top 5? let's show all aggregated if none selected is too messy, so maybe force selection or show total)
      // If compareServiceIds is empty, we can show Total. If not empty, show breakdown.
      if (compareServiceIds.length > 0 && !compareServiceIds.includes(apt.serviceId)) return;
      const date = new Date(apt.date);
      const key = getKey(date);
      const service = services.find(s => s.id === apt.serviceId);
      if (!dataMap[key]) {
        dataMap[key] = {
          name: key,
          totalCount: 0,
          totalRevenue: 0
        };
      }
      if (service) {
        // Count
        dataMap[key][service.name] = (dataMap[key][service.name] || 0) + 1;
        // Revenue (we can create a separate dataset for revenue or mix them, let's keep them separate in structure but calculated here)
        dataMap[key][`${service.name}_revenue`] = (dataMap[key][`${service.name}_revenue`] || 0) + service.price;
        dataMap[key].totalCount += 1;
        dataMap[key].totalRevenue += service.price;
      }
    });
    // Sort by date key
    return Object.values(dataMap).sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [filteredAppointments, groupBy, compareServiceIds, services]);
  // 3. Summary Table Data
  const summaryData = useMemo(() => {
    const summary: Record<string, {
      count: number;
      revenue: number;
      name: string;
    }> = {};
    // Initialize for selected services to ensure they appear even if 0
    if (compareServiceIds.length > 0) {
      compareServiceIds.forEach(id => {
        const s = services.find(ser => ser.id === id);
        if (s) summary[id] = {
          count: 0,
          revenue: 0,
          name: s.name
        };
      });
    } else {
      // If none selected, initialize all
      services.forEach(s => {
        summary[s.id] = {
          count: 0,
          revenue: 0,
          name: s.name
        };
      });
    }
    filteredAppointments.forEach(apt => {
      if (compareServiceIds.length > 0 && !compareServiceIds.includes(apt.serviceId)) return;
      const service = services.find(s => s.id === apt.serviceId);
      if (service) {
        if (!summary[service.id]) summary[service.id] = {
          count: 0,
          revenue: 0,
          name: service.name
        };
        summary[service.id].count += 1;
        summary[service.id].revenue += service.price;
      }
    });
    return Object.values(summary).sort((a, b) => b.revenue - a.revenue);
  }, [filteredAppointments, compareServiceIds, services]);
  // --- HANDLERS ---
  const openNewForm = () => {
    if (!canCreate('services')) return;
    setSelectedService(null);
    setFormData({
      name: '',
      duration: 30,
      price: '',
      description: '',
      doctorIds: []
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (service: Service) => {
    if (!canEdit('services')) return;
    setSelectedService(service);
    setFormData({
      name: service.name,
      duration: service.duration,
      price: String(service.price),
      description: service.description,
      doctorIds: service.doctorIds || []
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (service: Service) => {
    if (!canDelete('services')) return;
    setSelectedService(service);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (formData.duration <= 0) errors.duration = 'La duración debe ser mayor a 0';
    const priceNum = parseInt(formData.price, 10);
    if (isNaN(priceNum) || priceNum < 0) errors.price = 'El precio no puede ser negativo';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    const dataToSave = {
      name: formData.name,
      duration: formData.duration,
      price: priceNum,
      description: formData.description,
      doctorIds: formData.doctorIds
    };
    if (selectedService) {
      updateService(selectedService.id, dataToSave);
    } else {
      addService(dataToSave);
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (!canDelete('services')) return;
    if (selectedService) {
      deleteService(selectedService.id);
      setIsDeleteConfirmOpen(false);
    }
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportServicesToExcel(services);
    setIsExporting(false);
  };
  const toggleDoctorSelection = (doctorId: string) => {
    setFormData(prev => {
      const currentIds = prev.doctorIds || [];
      return currentIds.includes(doctorId) ? {
        ...prev,
        doctorIds: currentIds.filter(id => id !== doctorId)
      } : {
        ...prev,
        doctorIds: [...currentIds, doctorId]
      };
    });
  };
  const toggleCompareService = (serviceId: string) => {
    setCompareServiceIds(prev => prev.includes(serviceId) ? prev.filter(id => id !== serviceId) : [...prev, serviceId]);
  };
  const tabs = [{
    id: 'gestion',
    label: 'Gestión de Servicios',
    icon: <LayoutGridIcon className="w-4 h-4" data-id="element-1894" />
  }, ...(canViewStats ? [{
    id: 'estadisticas',
    label: 'Estadísticas y Reportes',
    icon: <BarChart3Icon className="w-4 h-4" data-id="element-1895" />
  }] : [])];
  return <div className="space-y-6" data-id="element-1896">
      <div className="flex items-center justify-between" data-id="element-1897">
        <div data-id="element-1898">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1899">
            Servicios
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1900">
            Administración y análisis de servicios médicos
          </p>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-1901">
        {/* ==================== TAB 1: GESTIÓN ==================== */}
        <TabPanel id="gestion" activeTab={activeTab} data-id="element-1902">
          <div className="space-y-6" data-id="element-1903">
            {/* Actions Bar */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center" data-id="element-1904">
              <div className="flex gap-2" data-id="element-1905">
                {canCreate('services') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1907" />} onClick={openNewForm} data-id="element-1906">
                    Nuevo Servicio
                  </Button>}
              </div>

              <div className="flex gap-2 w-full sm:w-auto" data-id="element-1908">
                <div className="relative flex-1 sm:w-64" data-id="element-1909">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1910" />
                  <Input placeholder="Buscar servicios..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-id="element-1911" />
                </div>
              </div>
            </div>

            {/* Export Card - Only for Admin */}
            {isAdmin && <Card className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 border-orange-200 dark:border-orange-800" data-id="element-1912">
                <div className="flex items-center justify-between" data-id="element-1913">
                  <div className="flex items-center gap-4" data-id="element-1914">
                    <div className="w-14 h-14 rounded-xl bg-orange-500 dark:bg-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/20" data-id="element-1915">
                      <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-1916" />
                    </div>
                    <div data-id="element-1917">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-1918">
                        Exportar Base de Datos
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1919">
                        Descarga todos los servicios en formato Excel
                      </p>
                    </div>
                  </div>
                  <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1921" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-orange-500/20 hover:shadow-xl hover:shadow-orange-500/30 transition-all duration-200" data-id="element-1920">
                    {isExporting ? 'Exportando...' : 'Descargar Excel'}
                  </Button>
                </div>
              </Card>}

            {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-1922">
                <LockIcon className="w-4 h-4" data-id="element-1923" />
                <span data-id="element-1924">
                  Solo tienes permisos de lectura. No puedes crear, editar o
                  eliminar servicios.
                </span>
              </div>}

            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1925">
              {paginatedServices.length === 0 ? <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-1926">
                  No se encontraron servicios
                </div> : paginatedServices.map(service => <Card key={service.id} className="flex flex-col h-full hover:shadow-md transition-shadow" data-id="element-1927">
                    <div className="flex items-start justify-between mb-4" data-id="element-1928">
                      <div data-id="element-1929">
                        <h3 className="font-semibold text-gray-900 dark:text-white text-lg" data-id="element-1930">
                          {service.name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1" data-id="element-1931">
                          <Badge variant="info" className="text-xs" data-id="element-1932">
                            {service.duration} min
                          </Badge>
                          <span className="text-xl font-bold text-sky-600 dark:text-sky-400" data-id="element-1933">
                            {formatCurrency(service.price)}
                          </span>
                        </div>
                      </div>
                      {!readOnly && <div className="flex gap-1" data-id="element-1934">
                          {canEdit('services') && <button onClick={() => openEditForm(service)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar servicio" data-id="element-1935">
                              <EditIcon className="w-4 h-4" data-id="element-1936" />
                            </button>}
                          {canDelete('services') && <button onClick={() => openDeleteConfirm(service)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Eliminar servicio" data-id="element-1937">
                              <TrashIcon className="w-4 h-4" data-id="element-1938" />
                            </button>}
                        </div>}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex-grow line-clamp-3" data-id="element-1939">
                      {service.description || 'Sin descripción'}
                    </p>
                    <div className="pt-4 border-t border-gray-100 dark:border-gray-700 mt-auto" data-id="element-1940">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1" data-id="element-1941">
                        <UserIcon className="w-3 h-3" data-id="element-1942" /> Doctores Asignados:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1943">
                        {service.doctorIds && service.doctorIds.length > 0 ? service.doctorIds.map(docId => {
                    const doctor = getDoctor(docId);
                    return doctor ? <span key={docId} className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300" data-id="element-1944">
                                Dr. {doctor.name}
                              </span> : null;
                  }) : <span className="text-xs text-gray-400 italic" data-id="element-1945">
                            Ningún doctor asignado
                          </span>}
                      </div>
                    </div>
                  </Card>)}
            </div>

            {/* Pagination */}
            <Pagination currentPage={servicesPage} totalPages={servicesTotalPages} totalItems={servicesTotalItems} startIndex={servicesStartIndex} endIndex={servicesEndIndex} onPageChange={servicesGoToPage} onNext={servicesNextPage} onPrev={servicesPrevPage} data-id="element-1946" />
          </div>
        </TabPanel>

        {/* ==================== TAB 2: ESTADÍSTICAS ==================== */}
        <TabPanel id="estadisticas" activeTab={activeTab} data-id="element-1947">
          <div className="space-y-6" data-id="element-1948">
            {/* 1. FILTERS SECTION */}
            <Card data-id="element-1949">
              <CardHeader data-id="element-1950">
                <CardTitle className="flex items-center gap-2 text-base" data-id="element-1951">
                  <FilterIcon className="w-4 h-4" data-id="element-1952" /> Filtros de Análisis
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-1953">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1954">
                  {/* Date Range */}
                  <div className="space-y-2" data-id="element-1955">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1956">
                      Rango de Fecha
                    </label>
                    <div className="flex items-center gap-2" data-id="element-1957">
                      <Input type="date" value={dateRange.start} onChange={e => setDateRange(prev => ({
                      ...prev,
                      start: e.target.value
                    }))} max={dateRange.end || getToday()} data-id="element-1958" />
                      <span className="text-gray-400" data-id="element-1959">-</span>
                      <Input type="date" value={dateRange.end} onChange={e => setDateRange(prev => ({
                      ...prev,
                      end: e.target.value
                    }))} max={getToday()} data-id="element-1960" />
                    </div>
                  </div>

                  {/* Group By */}
                  <div className="space-y-2" data-id="element-1961">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1962">
                      Agrupar Por
                    </label>
                    <Select value={groupBy} onChange={setGroupBy} options={[{
                    value: 'day',
                    label: 'Día'
                  }, {
                    value: 'week',
                    label: 'Semana'
                  }, {
                    value: 'month',
                    label: 'Mes'
                  }]} data-id="element-1963" />
                  </div>

                  {/* Service Selector */}
                  <div className="space-y-2" data-id="element-1964">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1965">
                      Filtrar Servicios
                    </label>
                    <div className="relative" data-id="element-1966">
                      <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1967" />
                      <input type="text" placeholder="Buscar para filtrar..." value={compareSearchTerm} onChange={e => setCompareSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent" data-id="element-1968" />
                    </div>
                    {/* Selected Tags */}
                    {compareServiceIds.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-1969">
                        {compareServiceIds.map((id, index) => {
                      const svc = services.find(s => s.id === id);
                      if (!svc) return null;
                      return <span key={id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium text-white" style={{
                        backgroundColor: COMPARE_COLORS[index % COMPARE_COLORS.length]
                      }} data-id="element-1970">
                              {svc.name}
                              <button onClick={() => toggleCompareService(id)} className="hover:opacity-75" data-id="element-1971">
                                <XIcon className="w-3 h-3" data-id="element-1972" />
                              </button>
                            </span>;
                    })}
                        <button onClick={() => setCompareServiceIds([])} className="text-xs text-gray-500 hover:text-red-500 px-1" data-id="element-1973">
                          Limpiar
                        </button>
                      </div>}
                    {/* Dropdown list (simplified as scrollable list) */}
                    <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg mt-2 p-1" data-id="element-1974">
                      {filteredCompareServices.map(service => <label key={service.id} className="flex items-center gap-2 p-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer" data-id="element-1975">
                          <input type="checkbox" checked={compareServiceIds.includes(service.id)} onChange={() => toggleCompareService(service.id)} className="w-3.5 h-3.5 text-sky-600 rounded border-gray-300" data-id="element-1976" />
                          <span className="text-xs text-gray-700 dark:text-gray-300 truncate" data-id="element-1977">
                            {service.name}
                          </span>
                        </label>)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 2. CHARTS SECTION */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1978">
              {/* Quantity Chart */}
              <Card data-id="element-1979">
                <CardHeader data-id="element-1980">
                  <CardTitle className="flex items-center gap-2" data-id="element-1981">
                    <BarChart3Icon className="w-5 h-5 text-sky-600" data-id="element-1982" />
                    Cantidad de Servicios
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-1983">
                  <div className="h-[300px] w-full" data-id="element-1984">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-1985">
                      <LineChart data={chartData} margin={{
                      top: 5,
                      right: 20,
                      left: 0,
                      bottom: 5
                    }} data-id="element-1986">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-1987" />
                        <XAxis dataKey="name" stroke="#6b7280" fontSize={11} data-id="element-1988" />
                        <YAxis stroke="#6b7280" fontSize={11} allowDecimals={false} data-id="element-1989" />
                        <Tooltip contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }} data-id="element-1990" />
                        <Legend data-id="element-1991" />
                        {compareServiceIds.length > 0 ? compareServiceIds.map((id, index) => {
                        const svc = services.find(s => s.id === id);
                        if (!svc) return null;
                        return <Line key={id} type="monotone" dataKey={svc.name} stroke={COMPARE_COLORS[index % COMPARE_COLORS.length]} strokeWidth={2} dot={{
                          r: 3
                        }} data-id="element-1992" />;
                      }) : <Line type="monotone" dataKey="totalCount" name="Total" stroke="#3b82f6" strokeWidth={2} dot={{
                        r: 3
                      }} data-id="element-1993" />}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Revenue Chart */}
              <Card data-id="element-1994">
                <CardHeader data-id="element-1995">
                  <CardTitle className="flex items-center gap-2" data-id="element-1996">
                    <DollarSignIcon className="w-5 h-5 text-green-600" data-id="element-1997" />
                    Ingresos por Servicio
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-1998">
                  <div className="h-[300px] w-full" data-id="element-1999">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-2000">
                      <BarChart data={chartData} margin={{
                      top: 5,
                      right: 20,
                      left: 0,
                      bottom: 5
                    }} data-id="element-2001">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-2002" />
                        <XAxis dataKey="name" stroke="#6b7280" fontSize={11} data-id="element-2003" />
                        <YAxis stroke="#6b7280" fontSize={11} tickFormatter={v => `$${v}`} data-id="element-2004" />
                        <Tooltip contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }} formatter={(value: number) => [formatCurrency(value), '']} data-id="element-2005" />
                        <Legend data-id="element-2006" />
                        {compareServiceIds.length > 0 ? compareServiceIds.map((id, index) => {
                        const svc = services.find(s => s.id === id);
                        if (!svc) return null;
                        return <Bar key={id} dataKey={`${svc.name}_revenue`} name={svc.name} fill={COMPARE_COLORS[index % COMPARE_COLORS.length]} radius={[4, 4, 0, 0]} stackId="a" data-id="element-2007" />;
                      }) : <Bar dataKey="totalRevenue" name="Total Ingresos" fill="#10b981" radius={[4, 4, 0, 0]} data-id="element-2008" />}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* 3. SUMMARY TABLE */}
            <Card data-id="element-2009">
              <CardHeader data-id="element-2010">
                <CardTitle className="flex items-center gap-2" data-id="element-2011">
                  <ListIcon className="w-5 h-5 text-gray-600" data-id="element-2012" />
                  Resumen del Periodo
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-2013">
                <div className="overflow-x-auto" data-id="element-2014">
                  <table className="w-full" data-id="element-2015">
                    <thead data-id="element-2016">
                      <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-2017">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-2018">
                          Servicio
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-2019">
                          Cantidad
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-2020">
                          Ingresos Totales
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-2021">
                          Promedio / Cita
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2022">
                      {summaryData.length === 0 ? <tr data-id="element-2023">
                          <td colSpan={4} className="py-8 text-center text-gray-500" data-id="element-2024">
                            No hay datos para el periodo seleccionado
                          </td>
                        </tr> : summaryData.map((item, idx) => <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-2025">
                            <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white" data-id="element-2026">
                              <div className="flex items-center gap-2" data-id="element-2027">
                                <div className="w-2 h-2 rounded-full" style={{
                            backgroundColor: compareServiceIds.length > 0 ? COMPARE_COLORS[compareServiceIds.indexOf(services.find(s => s.name === item.name)?.id || '') % COMPARE_COLORS.length] : '#3b82f6'
                          }} data-id="element-2028" />
                                {item.name}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300 text-right" data-id="element-2029">
                              {item.count}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-medium text-right" data-id="element-2030">
                              {formatCurrency(item.revenue)}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400 text-right" data-id="element-2031">
                              {item.count > 0 ? formatCurrency(item.revenue / item.count) : '-'}
                            </td>
                          </tr>)}
                      {/* Total Row */}
                      {summaryData.length > 0 && <tr className="bg-gray-50 dark:bg-gray-800 font-semibold" data-id="element-2032">
                          <td className="py-3 px-4 text-sm text-gray-900 dark:text-white" data-id="element-2033">
                            TOTAL
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-900 dark:text-white text-right" data-id="element-2034">
                            {summaryData.reduce((sum, item) => sum + item.count, 0)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-900 dark:text-white text-right" data-id="element-2035">
                            {formatCurrency(summaryData.reduce((sum, item) => sum + item.revenue, 0))}
                          </td>
                          <td className="py-3 px-4" data-id="element-2036"></td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabPanel>
      </Tabs>

      {/* MODALS (Shared for Management) */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedService ? 'Editar Servicio' : 'Nuevo Servicio'} size="md" icon={<BriefcaseIcon className="w-5 h-5" data-id="element-2038" />} accentColor="purple" data-id="element-2037">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2039">
          {/* Service Info */}
          <div data-id="element-2040">
            <div className="flex items-center gap-2 mb-4" data-id="element-2041">
              <span className="w-1.5 h-5 rounded-full bg-purple-500" data-id="element-2042"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2043">
                Información del Servicio
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2044">
              <Input label="Nombre del Servicio" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: Consulta General" data-id="element-2045" />
              <div className="grid grid-cols-2 gap-4" data-id="element-2046">
                <Input type="number" label="Duración (minutos)" value={formData.duration} onChange={e => setFormData({
                ...formData,
                duration: parseInt(e.target.value) || 0
              })} error={formErrors.duration} min={1} data-id="element-2047" />
                <Input type="number" label="Precio (DOP)" value={formData.price} onChange={e => setFormData({
                ...formData,
                price: e.target.value
              })} error={formErrors.price} min={0} step="1" data-id="element-2048" />
              </div>
              <Textarea label="Descripción" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} rows={3} placeholder="Descripción del servicio..." data-id="element-2049" />
            </div>
          </div>

          {/* Doctor Assignment */}
          <div data-id="element-2050">
            <div className="flex items-center gap-2 mb-4" data-id="element-2051">
              <span className="w-1.5 h-5 rounded-full bg-indigo-500" data-id="element-2052"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2053">
                Doctores Asignados
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2054">
              <div className="max-h-48 overflow-y-auto space-y-1" data-id="element-2055">
                {doctors.length === 0 ? <p className="text-sm text-gray-500 italic py-2" data-id="element-2056">
                    No hay doctores registrados
                  </p> : doctors.map(doctor => {
                const isSelected = formData.doctorIds.includes(doctor.id);
                return <label key={doctor.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-white dark:bg-gray-800 border border-sky-100 dark:border-sky-800/30 shadow-sm' : 'hover:bg-white dark:hover:bg-gray-800 border border-transparent'}`} data-id="element-2057">
                        <div className="relative" data-id="element-2058">
                          <input type="checkbox" checked={isSelected} onChange={() => toggleDoctorSelection(doctor.id)} className="sr-only" data-id="element-2059" />
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-sky-500 border-sky-500 dark:bg-sky-600 dark:border-sky-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500'}`} data-id="element-2060">
                            {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-2061">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-2062" />
                              </svg>}
                          </div>
                        </div>
                        <span className={`text-sm font-medium ${isSelected ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-2063">
                          Dr. {doctor.name}
                        </span>
                      </label>;
              })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-2064">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-2065">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-2066">
              {selectedService ? 'Guardar Cambios' : 'Crear Servicio'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-2067">
        <div className="space-y-4" data-id="element-2068">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-2069">
            ¿Está seguro de que desea eliminar el servicio "
            {selectedService?.name}"?
          </p>
          <div className="flex justify-end gap-3" data-id="element-2070">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-2071">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-2072">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}